import { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeftIcon, 
  CheckIcon,
  AdjustmentsHorizontalIcon,
  SparklesIcon,
  RectangleStackIcon,
  FaceSmileIcon
} from '@heroicons/react/24/outline';

interface Overlay {
  id: string;
  type: 'text' | 'emoji' | 'icon';
  content: string;
  x: number;
  y: number;
  fontSize?: number;
  fontFamily?: string;
  color?: string;
}

interface StampBorder {
  id: string;
  name: string;
  preview: string;
  country: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
}

interface StampImageEditorProps {
  imageUrl: string;
  onSave: (editedImageUrl: string) => void;
  onBack: () => void;
}

// Stamp border templates matching the sprite style
const STAMP_BORDERS: StampBorder[] = [
  {
    id: 'none',
    name: 'No Border',
    preview: '🖼️',
    country: 'none',
    primaryColor: '#000000',
    secondaryColor: '#FFFFFF',
    accentColor: '#CCCCCC',
    backgroundColor: '#FFFFFF'
  },
  {
    id: 'japan',
    name: 'Japan',
    preview: '🇯🇵',
    country: 'japan',
    primaryColor: '#4A90E2',
    secondaryColor: '#FFB6C1',
    accentColor: '#FFD700',
    backgroundColor: '#E3F2FD'
  },
  {
    id: 'france',
    name: 'France',
    preview: '🇫🇷',
    country: 'france',
    primaryColor: '#1E3A8A',
    secondaryColor: '#FCD34D',
    accentColor: '#EF4444',
    backgroundColor: '#EFF6FF'
  },
  {
    id: 'italy',
    name: 'Italy',
    preview: '🇮🇹',
    country: 'italy',
    primaryColor: '#16A34A',
    secondaryColor: '#EF4444',
    accentColor: '#FCD34D',
    backgroundColor: '#F0FDF4'
  },
  {
    id: 'egypt',
    name: 'Egypt',
    preview: '🇪🇬',
    country: 'egypt',
    primaryColor: '#F59E0B',
    secondaryColor: '#0EA5E9',
    accentColor: '#FCD34D',
    backgroundColor: '#FFF7ED'
  },
  {
    id: 'brazil',
    name: 'Brazil',
    preview: '🇧🇷',
    country: 'brazil',
    primaryColor: '#16A34A',
    secondaryColor: '#FCD34D',
    accentColor: '#0EA5E9',
    backgroundColor: '#F0FDF4'
  },
  {
    id: 'australia',
    name: 'Australia',
    preview: '🇦🇺',
    country: 'australia',
    primaryColor: '#EF4444',
    secondaryColor: '#F97316',
    accentColor: '#0EA5E9',
    backgroundColor: '#FFF1F2'
  },
  {
    id: 'india',
    name: 'India',
    preview: '🇮🇳',
    country: 'india',
    primaryColor: '#F97316',
    secondaryColor: '#FFB6C1',
    accentColor: '#16A34A',
    backgroundColor: '#FFF7ED'
  },
  {
    id: 'mexico',
    name: 'Mexico',
    preview: '🇲🇽',
    country: 'mexico',
    primaryColor: '#16A34A',
    secondaryColor: '#EF4444',
    accentColor: '#FCD34D',
    backgroundColor: '#FFFBEB'
  },
  {
    id: 'greece',
    name: 'Greece',
    preview: '🇬🇷',
    country: 'greece',
    primaryColor: '#0EA5E9',
    secondaryColor: '#FFFFFF',
    accentColor: '#FCD34D',
    backgroundColor: '#F0F9FF'
  },
  {
    id: 'china',
    name: 'China',
    preview: '🇨🇳',
    country: 'china',
    primaryColor: '#EF4444',
    secondaryColor: '#FCD34D',
    accentColor: '#000000',
    backgroundColor: '#FFF7ED'
  }
];

const FILTERS = [
  { id: 'normal', name: 'Normal', filter: '' },
  { id: 'grayscale', name: 'Grayscale', filter: 'grayscale(100%)' },
  { id: 'sepia', name: 'Sepia', filter: 'sepia(100%)' },
  { id: 'vintage', name: 'Vintage', filter: 'sepia(50%) contrast(110%) brightness(110%)' },
  { id: 'warm', name: 'Warm', filter: 'sepia(30%) saturate(130%)' },
  { id: 'cool', name: 'Cool', filter: 'hue-rotate(180deg) saturate(120%)' },
  { id: 'dramatic', name: 'Dramatic', filter: 'contrast(150%) brightness(90%)' },
  { id: 'faded', name: 'Faded', filter: 'opacity(80%) contrast(90%)' },
  // Stamp-like overlays (overlay applied in canvas pass below)
  { id: 'stamp_red', name: 'Rubber Stamp (Red)', filter: 'contrast(110%) saturate(105%)' },
  { id: 'stamp_blue', name: 'Rubber Stamp (Blue)', filter: 'contrast(110%) saturate(105%)' },
  { id: 'halftone', name: 'Halftone', filter: 'contrast(120%) brightness(95%)' },
  { id: 'aged_ink', name: 'Aged Ink', filter: 'sepia(35%) contrast(115%) brightness(98%)' }
];

const EMOJIS = [
  '✈️', '🗺️', '🏖️', '🏔️', '🌅', '🌄', '🌠', '🌌', '🌃', '🏙️', '🌁', '🌉',
  '🗼', '🗽', '🗿', '⛩️', '🕌', '⛪', '🏰', '🏯', '🏟️', '🎡', '🎢', '🎠',
  '🎪', '🎭', '🎨', '🎬', '🎤', '🎧', '🎼', '🎹', '🎺', '🎷', '🎸', '🎻'
];

// Utility functions
const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

const drawRoundedRectPath = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const placeCenters = (length: number, radius: number, gap: number): number[] => {
  const usable = length - radius * 2;
  const count = Math.max(1, Math.floor(usable / gap) + 1);
  const leftover = usable - (count - 1) * gap;
  const start = radius + Math.round(leftover / 2);
  return Array.from({ length: count }, (_, i) => Math.round(start + i * gap));
};

// Cached noise pattern for paper texture
let cachedNoisePattern: CanvasPattern | null = null;

const getPaperNoisePattern = (ctx: CanvasRenderingContext2D): CanvasPattern | null => {
  if (cachedNoisePattern) return cachedNoisePattern;
  
  const noise = document.createElement('canvas');
  noise.width = noise.height = 64;
  const nctx = noise.getContext('2d');
  if (!nctx) return null;
  
  const imgData = nctx.createImageData(64, 64);
  for (let i = 0; i < imgData.data.length; i += 4) {
    const v = 242 + Math.floor(Math.random() * 8); // Near-ivory variation
    imgData.data[i] = v;
    imgData.data[i + 1] = v;
    imgData.data[i + 2] = v;
    imgData.data[i + 3] = 18; // ~7% opacity
  }
  nctx.putImageData(imgData, 0, 0);
  cachedNoisePattern = ctx.createPattern(noise, 'repeat');
  return cachedNoisePattern;
};

export function StampImageEditor({ imageUrl, onSave, onBack }: StampImageEditorProps) {
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [activeTab, setActiveTab] = useState<'adjust' | 'filters' | 'stamps' | 'stickers'>('stamps');
  const [selectedBorder, setSelectedBorder] = useState<string>('none');
  const [selectedFilter, setSelectedFilter] = useState<string>('normal');
  
  // Image adjustment states
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [blur, setBlur] = useState(0);
  
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  // Draw illustrated border around image
  const drawIllustratedBorder = (
    ctx: CanvasRenderingContext2D,
    border: StampBorder,
    canvasWidth: number,
    canvasHeight: number,
    photoX: number,
    photoY: number,
    photoWidth: number,
    photoHeight: number
  ) => {
    // Do NOT paint a full-canvas background here; the paper layer is ivory.
    // Country elements should decorate on top of the paper.
    
    // Draw country-specific decorative elements
    switch (border.country) {
      case 'japan':
        drawJapanBorder(ctx, canvasWidth, canvasHeight, photoX, photoY, photoWidth, photoHeight, border);
        break;
      case 'france':
        drawFranceBorder(ctx, canvasWidth, canvasHeight, photoX, photoY, photoWidth, photoHeight, border);
        break;
      case 'italy':
        drawItalyBorder(ctx, canvasWidth, canvasHeight, photoX, photoY, photoWidth, photoHeight, border);
        break;
      case 'egypt':
        drawEgyptBorder(ctx, canvasWidth, canvasHeight, photoX, photoY, photoWidth, photoHeight, border);
        break;
      case 'brazil':
        drawBrazilBorder(ctx, canvasWidth, canvasHeight, photoX, photoY, photoWidth, photoHeight, border);
        break;
      case 'australia':
        drawAustraliaBorder(ctx, canvasWidth, canvasHeight, photoX, photoY, photoWidth, photoHeight, border);
        break;
      case 'india':
        drawIndiaBorder(ctx, canvasWidth, canvasHeight, photoX, photoY, photoWidth, photoHeight, border);
        break;
      case 'mexico':
        drawMexicoBorder(ctx, canvasWidth, canvasHeight, photoX, photoY, photoWidth, photoHeight, border);
        break;
      case 'greece':
        drawGreeceBorder(ctx, canvasWidth, canvasHeight, photoX, photoY, photoWidth, photoHeight, border);
        break;
      case 'china':
        drawChinaBorder(ctx, canvasWidth, canvasHeight, photoX, photoY, photoWidth, photoHeight, border);
        break;
    }
  };

  // Japan: Mount Fuji, cherry blossoms, sun
  const drawJapanBorder = (ctx: CanvasRenderingContext2D, w: number, h: number, _px: number, py: number, _pw: number, ph: number, border: StampBorder) => {
    // Top: Larger sun
    ctx.fillStyle = border.accentColor;
    ctx.beginPath();
    ctx.arc(w / 2, 70, 45, 0, Math.PI * 2); // Scaled up sun
    ctx.fill();
    
    // Cherry blossoms around top - fewer but larger (already scaled in helper)
    for (let i = 0; i < 4; i++) {
      const x = 100 + i * ((w - 200) / 3);
      drawCherryBlossom(ctx, x, 60, border.secondaryColor);
    }
    
    // Left: Larger Mount Fuji
    const fujiScale = 2.5;
    ctx.fillStyle = border.primaryColor;
    ctx.beginPath();
    ctx.moveTo(30, py + ph / 2 + 40 * fujiScale);
    ctx.lineTo(70, py + ph / 2 - 20 * fujiScale);
    ctx.lineTo(110, py + ph / 2 + 40 * fujiScale);
    ctx.closePath();
    ctx.fill();
    
    // Snow cap
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.moveTo(55, py + ph / 2 - 10 * fujiScale);
    ctx.lineTo(70, py + ph / 2 - 20 * fujiScale);
    ctx.lineTo(85, py + ph / 2 - 10 * fujiScale);
    ctx.closePath();
    ctx.fill();
    
    // Right: More cherry blossoms along side
    for (let i = 0; i < 3; i++) {
      drawCherryBlossom(ctx, w - 70, py + 80 + i * 100, border.secondaryColor);
    }
    
    // Bottom: Cherry blossoms - fewer but larger
    for (let i = 0; i < 4; i++) {
      const x = 100 + i * ((w - 200) / 3);
      drawCherryBlossom(ctx, x, h - 60, border.secondaryColor);
    }
  };

  // France: Eiffel Tower, croissants, baguettes
  const drawFranceBorder = (ctx: CanvasRenderingContext2D, w: number, h: number, _px: number, py: number, _pw: number, ph: number, border: StampBorder) => {
    // Left: Eiffel Tower
    ctx.fillStyle = border.primaryColor;
    ctx.strokeStyle = border.primaryColor;
    ctx.lineWidth = 3;
    
    // Tower base
    ctx.beginPath();
    ctx.moveTo(20, py + ph / 2 + 50);
    ctx.lineTo(30, py + ph / 2 - 50);
    ctx.lineTo(35, py + ph / 2 - 50);
    ctx.lineTo(45, py + ph / 2 + 50);
    ctx.closePath();
    ctx.fill();
    
    // Tower top
    ctx.beginPath();
    ctx.moveTo(30, py + ph / 2 - 50);
    ctx.lineTo(32.5, py + ph / 2 - 70);
    ctx.lineTo(35, py + ph / 2 - 50);
    ctx.fill();
    
    // Top: Croissants
    for (let i = 0; i < 5; i++) {
      const x = 80 + i * ((w - 160) / 4);
      drawCroissant(ctx, x, 40, border.secondaryColor);
    }
    
    // Right: Baguettes
    for (let i = 0; i < 3; i++) {
      drawBaguette(ctx, w - 40, py + 80 + i * 100, border.secondaryColor);
    }
    
    // Bottom: More croissants
    for (let i = 0; i < 5; i++) {
      const x = 80 + i * ((w - 160) / 4);
      drawCroissant(ctx, x, h - 40, border.secondaryColor);
    }
  };

  // Italy: Leaning Tower, pasta, pizza
  const drawItalyBorder = (ctx: CanvasRenderingContext2D, w: number, h: number, _px: number, py: number, _pw: number, ph: number, border: StampBorder) => {
    // Left: Leaning Tower of Pisa
    ctx.fillStyle = border.primaryColor;
    ctx.save();
    ctx.translate(40, py + ph / 2);
    ctx.rotate(0.1);
    ctx.fillRect(-10, -60, 20, 120);
    // Tower levels
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2;
    for (let i = 0; i < 5; i++) {
      ctx.strokeRect(-10, -60 + i * 24, 20, 24);
    }
    ctx.restore();
    
    // Top: Pizza slices
    for (let i = 0; i < 4; i++) {
      const x = 100 + i * ((w - 200) / 3);
      drawPizzaSlice(ctx, x, 40, border.accentColor, border.secondaryColor);
    }
    
    // Right: Pasta
    for (let i = 0; i < 3; i++) {
      drawPasta(ctx, w - 40, py + 80 + i * 100, border.secondaryColor);
    }
    
    // Bottom: More pizza
    for (let i = 0; i < 4; i++) {
      const x = 100 + i * ((w - 200) / 3);
      drawPizzaSlice(ctx, x, h - 40, border.accentColor, border.secondaryColor);
    }
  };

  // Egypt: Pyramids, camels, palm trees
  const drawEgyptBorder = (ctx: CanvasRenderingContext2D, w: number, h: number, _px: number, py: number, _pw: number, ph: number, border: StampBorder) => {
    // Top: Sun
    ctx.fillStyle = border.primaryColor;
    ctx.beginPath();
    ctx.arc(w / 2, 35, 28, 0, Math.PI * 2);
    ctx.fill();
    
    // Left: Palm tree
    drawPalmTree(ctx, 40, py + ph / 2, border.primaryColor, border.secondaryColor);
    
    // Bottom: Pyramids and camels
    ctx.fillStyle = border.primaryColor;
    
    // Pyramid 1
    ctx.beginPath();
    ctx.moveTo(w / 2 - 80, h - 30);
    ctx.lineTo(w / 2 - 50, h - 70);
    ctx.lineTo(w / 2 - 20, h - 30);
    ctx.closePath();
    ctx.fill();
    
    // Pyramid 2
    ctx.beginPath();
    ctx.moveTo(w / 2 + 20, h - 30);
    ctx.lineTo(w / 2 + 50, h - 70);
    ctx.lineTo(w / 2 + 80, h - 30);
    ctx.closePath();
    ctx.fill();
    
    // Camels
    for (let i = 0; i < 3; i++) {
      drawCamel(ctx, 100 + i * 80, h - 35, border.primaryColor);
    }
    
    // Right: Palm tree
    drawPalmTree(ctx, w - 40, py + ph / 2, border.primaryColor, border.secondaryColor);
  };

  // Brazil: Christ the Redeemer, soccer balls, tropical leaves
  const drawBrazilBorder = (ctx: CanvasRenderingContext2D, w: number, h: number, _px: number, py: number, _pw: number, ph: number, border: StampBorder) => {
    // Top: Tropical leaves
    for (let i = 0; i < 5; i++) {
      const x = 70 + i * ((w - 140) / 4);
      drawTropicalLeaf(ctx, x, 40, border.primaryColor);
    }
    
    // Left: Christ the Redeemer
    ctx.fillStyle = '#CCCCCC';
    ctx.beginPath();
    // Body
    ctx.fillRect(25, py + ph / 2 - 30, 10, 50);
    // Arms
    ctx.fillRect(10, py + ph / 2 - 20, 40, 8);
    // Head
    ctx.arc(30, py + ph / 2 - 35, 8, 0, Math.PI * 2);
    ctx.fill();
    
    // Right: Soccer balls
    for (let i = 0; i < 4; i++) {
      drawSoccerBall(ctx, w - 35, py + 70 + i * 80, 15);
    }
    
    // Bottom: More tropical leaves
    for (let i = 0; i < 5; i++) {
      const x = 70 + i * ((w - 140) / 4);
      drawTropicalLeaf(ctx, x, h - 40, border.primaryColor);
    }
  };

  // Australia: Opera House, kangaroos
  const drawAustraliaBorder = (ctx: CanvasRenderingContext2D, w: number, h: number, _px: number, py: number, _pw: number, ph: number, border: StampBorder) => {
    // Bottom: Opera House
    ctx.fillStyle = '#FFFFFF';
    ctx.strokeStyle = border.primaryColor;
    ctx.lineWidth = 2;
    
    const centerX = w / 2;
    const baseY = h - 30;
    
    // Opera house shells
    ctx.beginPath();
    ctx.moveTo(centerX - 60, baseY);
    ctx.quadraticCurveTo(centerX - 50, baseY - 40, centerX - 40, baseY);
    ctx.fill();
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(centerX - 30, baseY);
    ctx.quadraticCurveTo(centerX - 15, baseY - 50, centerX, baseY);
    ctx.fill();
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(centerX + 10, baseY);
    ctx.quadraticCurveTo(centerX + 25, baseY - 50, centerX + 40, baseY);
    ctx.fill();
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(centerX + 50, baseY);
    ctx.quadraticCurveTo(centerX + 60, baseY - 40, centerX + 70, baseY);
    ctx.fill();
    ctx.stroke();
    
    // Left: Kangaroo
    drawKangaroo(ctx, 35, py + ph / 2, border.primaryColor);
    
    // Right: Kangaroo
    drawKangaroo(ctx, w - 35, py + ph / 2, border.primaryColor);
  };

  // India: Taj Mahal, elephants, lotus
  const drawIndiaBorder = (ctx: CanvasRenderingContext2D, w: number, h: number, _px: number, py: number, _pw: number, _ph: number, border: StampBorder) => {
    // Top: Taj Mahal
    ctx.fillStyle = '#FFFFFF';
    ctx.strokeStyle = border.primaryColor;
    ctx.lineWidth = 2;
    
    const centerX = w / 2;
    
    // Central dome
    ctx.beginPath();
    ctx.arc(centerX, 50, 20, Math.PI, 0);
    ctx.fill();
    ctx.stroke();
    
    // Minarets
    ctx.fillRect(centerX - 40, 40, 8, 30);
    ctx.fillRect(centerX + 32, 40, 8, 30);
    
    // Left: Elephants
    for (let i = 0; i < 2; i++) {
      drawElephant(ctx, 35, py + 100 + i * 120, border.primaryColor);
    }
    
    // Right: Lotus flowers
    for (let i = 0; i < 3; i++) {
      drawLotusFlower(ctx, w - 40, py + 80 + i * 100, border.secondaryColor);
    }
    
    // Bottom: More lotus
    for (let i = 0; i < 4; i++) {
      const x = 100 + i * ((w - 200) / 3);
      drawLotusFlower(ctx, x, h - 40, border.secondaryColor);
    }
  };

  // Mexico: Cacti, peppers, sombreros
  const drawMexicoBorder = (ctx: CanvasRenderingContext2D, w: number, h: number, _px: number, py: number, _pw: number, ph: number, border: StampBorder) => {
    // Left: Cactus
    drawCactus(ctx, 40, py + ph / 2, border.primaryColor);
    
    // Right: Cactus
    drawCactus(ctx, w - 40, py + ph / 2, border.primaryColor);
    
    // Top: Peppers
    for (let i = 0; i < 5; i++) {
      const x = 70 + i * ((w - 140) / 4);
      drawPepper(ctx, x, 40, border.secondaryColor);
    }
    
    // Bottom: Sombreros and peppers
    for (let i = 0; i < 3; i++) {
      const x = 120 + i * ((w - 240) / 2);
      drawSombrero(ctx, x, h - 40, border.accentColor, border.secondaryColor);
    }
  };

  // Greece: Columns, olives, vases
  const drawGreeceBorder = (ctx: CanvasRenderingContext2D, w: number, h: number, _px: number, py: number, _pw: number, ph: number, border: StampBorder) => {
    // Left: Greek column
    drawGreekColumn(ctx, 40, py + ph / 2, border.primaryColor);
    
    // Right: Greek column
    drawGreekColumn(ctx, w - 40, py + ph / 2, border.primaryColor);
    
    // Top: Greek pattern
    ctx.strokeStyle = border.primaryColor;
    ctx.lineWidth = 3;
    for (let i = 0; i < 8; i++) {
      const x = 80 + i * ((w - 160) / 7);
      drawGreekKey(ctx, x, 40, 20);
    }
    
    // Bottom: Olives and vases
    for (let i = 0; i < 4; i++) {
      const x = 100 + i * ((w - 200) / 3);
      drawGreekVase(ctx, x, h - 40, border.primaryColor, border.accentColor);
    }
  };

  // China: Great Wall, pandas, lanterns
  const drawChinaBorder = (ctx: CanvasRenderingContext2D, w: number, h: number, _px: number, py: number, _pw: number, ph: number, border: StampBorder) => {
    // Decorative background accents: bamboo and clouds for richness
    const drawBamboo = (bx: number, by: number, height: number) => {
      ctx.save();
      ctx.strokeStyle = '#2E7D32';
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(bx, by + height);
      ctx.stroke();
      // nodes
      ctx.lineWidth = 2;
      for (let i = 0; i < Math.floor(height / 28); i++) {
        ctx.beginPath();
        ctx.moveTo(bx - 10, by + i * 28);
        ctx.lineTo(bx + 10, by + i * 28);
        ctx.stroke();
      }
      // leaves
      ctx.fillStyle = '#43A047';
      for (let i = 0; i < 4; i++) {
        ctx.beginPath();
        ctx.ellipse(bx - 16, by + i * (height / 4), 6, 14, -0.6, 0, Math.PI * 2);
        ctx.ellipse(bx + 16, by + i * (height / 4) + 8, 6, 14, 0.6, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    };

    const drawCloud = (cx: number, cy: number, scale: number) => {
      ctx.save();
      ctx.fillStyle = '#FFE8B8';
      ctx.beginPath();
      ctx.arc(cx, cy, 16 * scale, 0, Math.PI * 2);
      ctx.arc(cx + 20 * scale, cy + 4 * scale, 12 * scale, 0, Math.PI * 2);
      ctx.arc(cx + 40 * scale, cy, 16 * scale, 0, Math.PI * 2);
      ctx.arc(cx + 24 * scale, cy - 10 * scale, 10 * scale, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };

    // Soft clouds
    drawCloud(w * 0.2, 70, 1.2);
    drawCloud(w * 0.7, 60, 1.0);

    // Bamboo clusters left/right
    drawBamboo(55, py + 40, ph + 60);
    drawBamboo(w - 60, py + 10, ph + 90);

    // Top: Lanterns - adjusted spacing for larger lanterns with a string
    const lanternCount = 3;
    for (let i = 0; i < lanternCount; i++) {
      const spacing = (w - 200) / (lanternCount + 1);
      const x = 100 + (i + 1) * spacing;
      // string
      ctx.save();
      ctx.strokeStyle = '#8B5E3C';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(x - spacing / 2, 58);
      ctx.quadraticCurveTo(x, 50, x + spacing / 2, 58);
      ctx.stroke();
      ctx.restore();
      drawLantern(ctx, x, 80, border.secondaryColor, border.primaryColor); // Lower Y for larger lanterns
    }
    
    // Left: Panda - adjusted position for larger panda
    drawPanda(ctx, 80, py + ph / 2);
    
    // Right: Panda - adjusted position for larger panda
    drawPanda(ctx, w - 120, py + ph / 2);
    
    // Bottom: Great Wall - scale up and add crenellations
    ctx.fillStyle = '#8B7355';
    ctx.strokeStyle = '#654321';
    ctx.lineWidth = 4;
    
    const wallY = h - 100;
    const wallHeight = 50;
    ctx.beginPath();
    ctx.moveTo(80, wallY + wallHeight);
    for (let i = 0; i < 5; i++) {
      const x = 80 + i * ((w - 160) / 4);
      const nextX = 80 + (i + 1) * ((w - 160) / 4);
      ctx.lineTo(x, wallY + (i % 2 === 0 ? 0 : 20));
      ctx.lineTo(nextX, wallY + (i % 2 === 0 ? 20 : 0));
    }
    ctx.lineTo(w - 80, wallY + wallHeight);
    ctx.lineTo(w - 80, wallY + wallHeight + 30);
    ctx.lineTo(80, wallY + wallHeight + 30);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // crenellations
    ctx.fillStyle = '#6D5844';
    const step = (w - 160) / 12;
    for (let i = 0; i <= 12; i++) {
      const cx = 80 + i * step;
      const cy = wallY + (i % 2 === 0 ? 6 : 18);
      ctx.fillRect(cx - 6, cy - 14, 12, 14);
    }
  };

  // Helper drawing functions for decorative elements - scaled up for sprite-accurate appearance
  const drawCherryBlossom = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => {
    const scale = 2.5; // Scale up cherry blossoms
    ctx.fillStyle = color;
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * Math.PI * 2;
      const px = x + Math.cos(angle) * 8 * scale;
      const py = y + Math.sin(angle) * 8 * scale;
      ctx.beginPath();
      ctx.ellipse(px, py, 5 * scale, 3 * scale, angle, 0, Math.PI * 2);
      ctx.fill();
    }
    // Center
    ctx.fillStyle = '#FCD34D';
    ctx.beginPath();
    ctx.arc(x, y, 2 * scale, 0, Math.PI * 2);
    ctx.fill();
  };

  const drawCroissant = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x - 8, y, 8, 0.5, Math.PI - 0.5);
    ctx.arc(x + 8, y, 8, Math.PI + 0.5, -0.5);
    ctx.closePath();
    ctx.fill();
    
    // Lines
    ctx.strokeStyle = '#D4A574';
    ctx.lineWidth = 1;
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(x - 6 + i * 6, y - 5);
      ctx.lineTo(x - 6 + i * 6, y + 5);
      ctx.stroke();
    }
  };

  const drawBaguette = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(Math.PI / 4);
    ctx.fillStyle = color;
    ctx.fillRect(-5, -15, 10, 30);
    
    // Diagonal cuts
    ctx.strokeStyle = '#D4A574';
    ctx.lineWidth = 2;
    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      ctx.moveTo(-5, -12 + i * 8);
      ctx.lineTo(5, -12 + i * 8);
      ctx.stroke();
    }
    ctx.restore();
  };

  const drawPizzaSlice = (ctx: CanvasRenderingContext2D, x: number, y: number, crustColor: string, cheeseColor: string) => {
    // Crust
    ctx.fillStyle = crustColor;
    ctx.beginPath();
    ctx.moveTo(x, y - 15);
    ctx.lineTo(x - 12, y + 10);
    ctx.lineTo(x + 12, y + 10);
    ctx.closePath();
    ctx.fill();
    
    // Cheese
    ctx.fillStyle = cheeseColor;
    ctx.beginPath();
    ctx.moveTo(x, y - 12);
    ctx.lineTo(x - 10, y + 8);
    ctx.lineTo(x + 10, y + 8);
    ctx.closePath();
    ctx.fill();
    
    // Pepperoni
    ctx.fillStyle = '#EF4444';
    ctx.beginPath();
    ctx.arc(x - 3, y - 3, 2, 0, Math.PI * 2);
    ctx.arc(x + 3, y + 2, 2, 0, Math.PI * 2);
    ctx.fill();
  };

  const drawPasta = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(x - 10, y - 10 + i * 8);
      ctx.quadraticCurveTo(x, y - 5 + i * 8, x - 10, y + i * 8);
      ctx.stroke();
    }
  };

  const drawPalmTree = (ctx: CanvasRenderingContext2D, x: number, y: number, trunkColor: string, leavesColor: string) => {
    // Trunk
    ctx.strokeStyle = trunkColor;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(x, y + 30);
    ctx.lineTo(x, y - 20);
    ctx.stroke();
    
    // Leaves
    ctx.fillStyle = leavesColor;
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      ctx.save();
      ctx.translate(x, y - 20);
      ctx.rotate(angle);
      ctx.beginPath();
      ctx.ellipse(0, -15, 5, 15, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  };

  const drawCamel = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => {
    ctx.fillStyle = color;
    // Body
    ctx.fillRect(x - 8, y, 16, 8);
    // Hump
    ctx.beginPath();
    ctx.arc(x, y, 6, Math.PI, 0);
    ctx.fill();
    // Legs
    ctx.fillRect(x - 6, y + 8, 2, 6);
    ctx.fillRect(x + 4, y + 8, 2, 6);
    // Head
    ctx.fillRect(x + 8, y + 2, 4, 4);
  };

  const drawTropicalLeaf = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => {
    ctx.fillStyle = color;
    ctx.save();
    ctx.translate(x, y);
    ctx.beginPath();
    ctx.ellipse(0, 0, 8, 15, Math.PI / 6, 0, Math.PI * 2);
    ctx.fill();
    
    // Leaf veins
    ctx.strokeStyle = '#065F46';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, -12);
    ctx.lineTo(0, 12);
    ctx.stroke();
    ctx.restore();
  };

  const drawSoccerBall = (ctx: CanvasRenderingContext2D, x: number, y: number, radius: number) => {
    // White circle
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    
    // Black pentagons
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.arc(x, y, radius / 3, 0, Math.PI * 2);
    ctx.fill();
    
    // Black lines
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(angle) * (radius / 3), y + Math.sin(angle) * (radius / 3));
      ctx.lineTo(x + Math.cos(angle) * radius, y + Math.sin(angle) * radius);
      ctx.stroke();
    }
  };

  const drawKangaroo = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => {
    ctx.fillStyle = color;
    // Body
    ctx.beginPath();
    ctx.ellipse(x, y, 12, 18, 0, 0, Math.PI * 2);
    ctx.fill();
    // Head
    ctx.beginPath();
    ctx.arc(x + 5, y - 15, 8, 0, Math.PI * 2);
    ctx.fill();
    // Ears
    ctx.beginPath();
    ctx.ellipse(x + 3, y - 22, 3, 6, -0.3, 0, Math.PI * 2);
    ctx.ellipse(x + 7, y - 22, 3, 6, 0.3, 0, Math.PI * 2);
    ctx.fill();
    // Tail
    ctx.beginPath();
    ctx.arc(x - 10, y + 10, 5, 0, Math.PI);
    ctx.fill();
    // Legs
    ctx.fillRect(x - 3, y + 15, 3, 8);
    ctx.fillRect(x + 5, y + 15, 3, 8);
  };

  const drawElephant = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => {
    ctx.fillStyle = color;
    // Body
    ctx.beginPath();
    ctx.ellipse(x, y, 15, 12, 0, 0, Math.PI * 2);
    ctx.fill();
    // Head
    ctx.beginPath();
    ctx.arc(x + 12, y - 5, 10, 0, Math.PI * 2);
    ctx.fill();
    // Trunk
    ctx.beginPath();
    ctx.moveTo(x + 18, y);
    ctx.quadraticCurveTo(x + 25, y + 5, x + 22, y + 12);
    ctx.lineWidth = 5;
    ctx.strokeStyle = color;
    ctx.stroke();
    // Ear
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(x + 12, y - 10, 8, 12, 0, 0, Math.PI * 2);
    ctx.fill();
  };

  const drawLotusFlower = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => {
    ctx.fillStyle = color;
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const px = x + Math.cos(angle) * 8;
      const py = y + Math.sin(angle) * 8;
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(angle);
      ctx.beginPath();
      ctx.ellipse(0, 0, 4, 8, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    // Center
    ctx.fillStyle = '#FCD34D';
    ctx.beginPath();
    ctx.arc(x, y, 4, 0, Math.PI * 2);
    ctx.fill();
  };

  const drawCactus = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => {
    ctx.fillStyle = color;
    // Main body
    ctx.fillRect(x - 5, y - 30, 10, 60);
    // Arms
    ctx.fillRect(x - 15, y - 10, 10, 20);
    ctx.fillRect(x + 5, y - 5, 10, 15);
    
    // Spikes
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1;
    for (let i = 0; i < 8; i++) {
      const py = y - 25 + i * 7;
      ctx.beginPath();
      ctx.moveTo(x - 5, py);
      ctx.lineTo(x - 7, py);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x + 5, py);
      ctx.lineTo(x + 7, py);
      ctx.stroke();
    }
  };

  const drawPepper = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(x, y - 8);
    ctx.quadraticCurveTo(x + 5, y - 5, x + 4, y + 8);
    ctx.quadraticCurveTo(x, y + 10, x - 4, y + 8);
    ctx.quadraticCurveTo(x - 5, y - 5, x, y - 8);
    ctx.fill();
    
    // Stem
    ctx.fillStyle = '#16A34A';
    ctx.fillRect(x - 1, y - 12, 2, 5);
  };

  const drawSombrero = (ctx: CanvasRenderingContext2D, x: number, y: number, color1: string, color2: string) => {
    // Brim
    ctx.fillStyle = color1;
    ctx.beginPath();
    ctx.ellipse(x, y, 20, 6, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Crown
    ctx.fillStyle = color2;
    ctx.beginPath();
    ctx.moveTo(x - 8, y);
    ctx.lineTo(x - 5, y - 12);
    ctx.lineTo(x + 5, y - 12);
    ctx.lineTo(x + 8, y);
    ctx.closePath();
    ctx.fill();
  };

  const drawGreekColumn = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => {
    ctx.fillStyle = color;
    // Capital
    ctx.fillRect(x - 12, y - 40, 24, 6);
    // Shaft
    ctx.fillRect(x - 8, y - 34, 16, 50);
    // Base
    ctx.fillRect(x - 12, y + 16, 24, 6);
    
    // Flutes
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1;
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(x - 4 + i * 4, y - 34);
      ctx.lineTo(x - 4 + i * 4, y + 16);
      ctx.stroke();
    }
  };

  const drawGreekKey = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number) => {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + size, y);
    ctx.lineTo(x + size, y + size / 2);
    ctx.lineTo(x + size / 2, y + size / 2);
    ctx.lineTo(x + size / 2, y + size);
    ctx.stroke();
  };

  const drawGreekVase = (ctx: CanvasRenderingContext2D, x: number, y: number, color1: string, color2: string) => {
    // Vase body
    ctx.fillStyle = color1;
    ctx.beginPath();
    ctx.moveTo(x - 8, y - 15);
    ctx.quadraticCurveTo(x - 12, y, x - 8, y + 15);
    ctx.lineTo(x + 8, y + 15);
    ctx.quadraticCurveTo(x + 12, y, x + 8, y - 15);
    ctx.closePath();
    ctx.fill();
    
    // Neck
    ctx.fillRect(x - 4, y - 20, 8, 5);
    
    // Handles
    ctx.strokeStyle = color1;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(x - 10, y - 5, 5, 0, Math.PI);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(x + 10, y - 5, 5, Math.PI, 0);
    ctx.stroke();
    
    // Decoration
    ctx.strokeStyle = color2;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x - 8, y);
    ctx.lineTo(x + 8, y);
    ctx.stroke();
  };

  const drawLantern = (ctx: CanvasRenderingContext2D, x: number, y: number, color1: string, color2: string) => {
    // Scale up lanterns to be 3.5× larger for sprite-accurate appearance
    const scale = 3.5;
    
    // Lantern body
    ctx.fillStyle = color1;
    ctx.beginPath();
    ctx.ellipse(x, y, 10 * scale, 15 * scale, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Horizontal lines
    ctx.strokeStyle = color2;
    ctx.lineWidth = 3 * scale;
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(x - 10 * scale, y - 10 * scale + i * 10 * scale);
      ctx.lineTo(x + 10 * scale, y - 10 * scale + i * 10 * scale);
      ctx.stroke();
    }
    
    // String
    ctx.strokeStyle = color2;
    ctx.lineWidth = 2 * scale;
    ctx.beginPath();
    ctx.moveTo(x, y - 15 * scale);
    ctx.lineTo(x, y - 35 * scale);
    ctx.stroke();
    
    // Tassel
    ctx.beginPath();
    ctx.moveTo(x, y + 15 * scale);
    ctx.lineTo(x, y + 25 * scale);
    ctx.stroke();
  };

  const drawPanda = (ctx: CanvasRenderingContext2D, x: number, y: number) => {
    // Scale up pandas to be 3× larger for sprite-accurate appearance
    const scale = 3;
    
    // Head
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.arc(x, y, 15 * scale, 0, Math.PI * 2);
    ctx.fill();
    
    // Ears
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.arc(x - 10 * scale, y - 10 * scale, 6 * scale, 0, Math.PI * 2);
    ctx.arc(x + 10 * scale, y - 10 * scale, 6 * scale, 0, Math.PI * 2);
    ctx.fill();
    
    // Eyes
    ctx.beginPath();
    ctx.ellipse(x - 5 * scale, y - 2 * scale, 4 * scale, 6 * scale, 0, 0, Math.PI * 2);
    ctx.ellipse(x + 5 * scale, y - 2 * scale, 4 * scale, 6 * scale, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Nose
    ctx.beginPath();
    ctx.arc(x, y + 5 * scale, 3 * scale, 0, Math.PI * 2);
    ctx.fill();
    
    // Body
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.ellipse(x, y + 25 * scale, 12 * scale, 15 * scale, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Arms/legs patches
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.arc(x - 10 * scale, y + 20 * scale, 5 * scale, 0, Math.PI * 2);
    ctx.arc(x + 10 * scale, y + 20 * scale, 5 * scale, 0, Math.PI * 2);
    ctx.fill();
  };

  // Draw paper background with ivory color, shadow, and noise texture
  const drawPaperBackground = (ctx: CanvasRenderingContext2D, width: number, height: number, perfRadius: number) => {
    // Draw soft shadow
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.18)';
    ctx.shadowBlur = 12;
    ctx.shadowOffsetY = 6;
    
    // Warm ivory paper fill
    ctx.fillStyle = '#FFF7E6';
    // Fill the entire canvas so that JPEG exports never turn outside alpha into black
    ctx.fillRect(0, 0, width, height);
    // Reinforce inner paper body (slightly brighter) to imply edge thickness
    ctx.fillStyle = '#FFF8EB';
    ctx.fillRect(perfRadius, perfRadius, width - perfRadius * 2, height - perfRadius * 2);
    ctx.restore();
    
    // Add subtle paper noise texture
    const noisePattern = getPaperNoisePattern(ctx);
    if (noisePattern) {
      ctx.save();
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = noisePattern;
      ctx.fillRect(perfRadius, perfRadius, width - perfRadius * 2, height - perfRadius * 2);
      ctx.restore();
    }
  };

  // Draw perforated edges with dynamic sizing and centered placement
  const drawPerforatedEdges = (ctx: CanvasRenderingContext2D, width: number, height: number, perfRadius: number, perfGap: number) => {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = '#000000';
    
    // Top edge - full circles cut out from top
    const topCenters = placeCenters(width, perfRadius, perfGap);
    topCenters.forEach(x => {
      ctx.beginPath();
      ctx.arc(x, 0, perfRadius, 0, Math.PI * 2);
      ctx.fill();
    });
    
    // Bottom edge - full circles cut out from bottom
    const bottomCenters = placeCenters(width, perfRadius, perfGap);
    bottomCenters.forEach(x => {
      ctx.beginPath();
      ctx.arc(x, height, perfRadius, 0, Math.PI * 2);
      ctx.fill();
    });
    
    // Left edge - full circles cut out from left
    const leftCenters = placeCenters(height, perfRadius, perfGap);
    leftCenters.forEach(y => {
      ctx.beginPath();
      ctx.arc(0, y, perfRadius, 0, Math.PI * 2);
      ctx.fill();
    });
    
    // Right edge - full circles cut out from right
    const rightCenters = placeCenters(height, perfRadius, perfGap);
    rightCenters.forEach(y => {
      ctx.beginPath();
      ctx.arc(width, y, perfRadius, 0, Math.PI * 2);
      ctx.fill();
    });
    
    ctx.globalCompositeOperation = 'source-over';
  };

  // Apply stamp border to image with layered rendering
  const applyStampBorder = useCallback(async (): Promise<string> => {
    return new Promise((resolve, reject) => {
      const border = STAMP_BORDERS.find(b => b.id === selectedBorder);
      if (!border || border.id === 'none') {
        resolve(imageUrl);
        return;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          // Dynamic sizing
          const photoWidth = img.width;
          const photoHeight = img.height;
          const minPhoto = Math.min(photoWidth, photoHeight);
          const borderWidth = Math.round(clamp(minPhoto * 0.16, 80, 140));
          
          const canvasWidth = photoWidth + (borderWidth * 2);
          const canvasHeight = photoHeight + (borderWidth * 2);
          const minDim = Math.min(canvasWidth, canvasHeight);
          
          // Perforation sizing - tuned to sprite parity
          const perfRadius = Math.round(clamp(minDim * 0.032, 16, 26));
          const perfGap = Math.round(perfRadius * 3.0);
          
          // Safe area for illustrations
          const safe = borderWidth - 24;
          
          // Inner frame dimensions (thicker, relative to borderWidth)
          const frameOuter = Math.round(borderWidth * 0.18); // white margin
          const frameInner = 12; // colored stroke
          const cornerRadius = 22;
          
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;

          // LAYER 1: Paper background with shadow and texture
          drawPaperBackground(ctx, canvasWidth, canvasHeight, perfRadius);

          // LAYER 2: Background illustrations (respecting safe area)
          const safePhotoX = safe;
          const safePhotoY = safe;
          const safePhotoW = canvasWidth - safe * 2;
          const safePhotoH = canvasHeight - safe * 2;
          drawIllustratedBorder(ctx, border, canvasWidth, canvasHeight, safePhotoX, safePhotoY, safePhotoW, safePhotoH);

          // LAYER 3: Perforations (cut holes in paper)
          drawPerforatedEdges(ctx, canvasWidth, canvasHeight, perfRadius, perfGap);

          // LAYER 4: Inner frame (white margin + colored frame)
          const photoX = borderWidth;
          const photoY = borderWidth;
          
          // Draw white outer margin
          ctx.save();
          ctx.fillStyle = '#FFFFFF';
          drawRoundedRectPath(ctx, photoX - frameOuter, photoY - frameOuter, photoWidth + frameOuter * 2, photoHeight + frameOuter * 2, cornerRadius);
          ctx.fill();
          ctx.restore();
          
          // Draw colored inner frame
          ctx.save();
          ctx.strokeStyle = border.primaryColor;
          ctx.lineWidth = frameInner;
          drawRoundedRectPath(ctx, photoX - frameOuter + frameInner/2, photoY - frameOuter + frameInner/2, photoWidth + (frameOuter - frameInner/2) * 2, photoHeight + (frameOuter - frameInner/2) * 2, cornerRadius);
          ctx.stroke();
          ctx.restore();

          // LAYER 5: Photo (clipped to inner frame)
          ctx.save();
          drawRoundedRectPath(ctx, photoX, photoY, photoWidth, photoHeight, cornerRadius - frameInner);
          ctx.clip();
          
          const baseFilter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) blur(${blur}px)`;
          const selectedFilterStyle = FILTERS.find(f => f.id === selectedFilter)?.filter || '';
          ctx.filter = selectedFilterStyle ? `${baseFilter} ${selectedFilterStyle}` : baseFilter;
          
          ctx.drawImage(img, photoX, photoY, photoWidth, photoHeight);
          ctx.filter = 'none';

          // Stamp-like overlay rendering modes
          if (selectedFilter === 'stamp_red' || selectedFilter === 'stamp_blue') {
            const color = selectedFilter === 'stamp_red' ? 'rgba(200, 20, 20, 0.28)' : 'rgba(30, 85, 200, 0.28)';
            ctx.save();
            ctx.globalCompositeOperation = 'multiply';
            ctx.strokeStyle = color;
            ctx.lineWidth = 14;
            ctx.setLineDash([18, 10]);
            drawRoundedRectPath(ctx, photoX + 20, photoY + 20, photoWidth - 40, photoHeight - 40, cornerRadius - frameInner - 8);
            ctx.stroke();
            ctx.restore();
          } else if (selectedFilter === 'halftone') {
            // Simple halftone dots
            ctx.save();
            ctx.globalAlpha = 0.18;
            ctx.globalCompositeOperation = 'multiply';
            ctx.fillStyle = '#000000';
            const step = 8;
            for (let y = photoY; y < photoY + photoHeight; y += step) {
              for (let x = photoX; x < photoX + photoWidth; x += step) {
                ctx.beginPath();
                ctx.arc(x + 3, y + 3, 1.5, 0, Math.PI * 2);
                ctx.fill();
              }
            }
            ctx.restore();
          } else if (selectedFilter === 'aged_ink') {
            ctx.save();
            ctx.globalCompositeOperation = 'multiply';
            const grad = ctx.createLinearGradient(photoX, photoY, photoX, photoY + photoHeight);
            grad.addColorStop(0, 'rgba(60,40,20,0.10)');
            grad.addColorStop(1, 'rgba(0,0,0,0.0)');
            ctx.fillStyle = grad;
            ctx.fillRect(photoX, photoY, photoWidth, photoHeight);
            ctx.restore();
          }
          ctx.restore();

          // LAYER 6: Foreground overlap for select borders (peek over photo)
          if (border.country === 'japan') {
            // A blossom overlapping top-left corner
            ctx.save();
            ctx.shadowColor = 'rgba(0,0,0,0.15)';
            ctx.shadowBlur = 6;
            drawCherryBlossom(ctx, photoX - frameOuter + 30, photoY - frameOuter + 30, border.secondaryColor);
            ctx.restore();
          } else if (border.country === 'china') {
            // A lantern tassel overlapping top edge
            ctx.save();
            ctx.strokeStyle = border.primaryColor;
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(photoX + photoWidth * 0.5, photoY - frameOuter);
            ctx.lineTo(photoX + photoWidth * 0.5, photoY - frameOuter - 18);
            ctx.stroke();
            ctx.restore();
          }

          // LAYER 7: User overlays (text/emoji)
          overlays.forEach(overlay => {
            ctx.save();
            ctx.font = `${overlay.fontSize || 24}px ${overlay.fontFamily || 'Arial'}`;
            ctx.fillStyle = overlay.color || '#000000';
            ctx.fillText(overlay.content, overlay.x + photoX, overlay.y + photoY);
            ctx.restore();
          });

          // EXPORT: Prefer PNG to keep perforations ivory/transparent
          resolve(canvas.toDataURL('image/png'));
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = (error) => {
        reject(error);
      };

      img.src = imageUrl;
    });
  }, [imageUrl, selectedBorder, selectedFilter, brightness, contrast, saturation, blur, overlays]);

  // Update preview
  useEffect(() => {
    if (selectedBorder !== 'none' && previewCanvasRef.current) {
      applyStampBorder().then(dataUrl => {
        const img = new Image();
        img.onload = () => {
          const canvas = previewCanvasRef.current;
          if (!canvas) return;
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          
          // Scale to fit preview
          const scale = Math.min(400 / img.width, 500 / img.height);
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        };
        img.src = dataUrl;
      });
    }
  }, [selectedBorder, applyStampBorder]);

  // Generate final image
  const generateFinalImage = async () => {
    try {
      const finalImageUrl = await applyStampBorder();
      onSave(finalImageUrl);
    } catch (error) {
      console.error('Error generating final image:', error);
      onSave(imageUrl);
    }
  };

  // Add overlay
  const addOverlay = (type: 'emoji', content: string) => {
    const newOverlay: Overlay = {
      id: `${type}-${Date.now()}`,
      type,
      content,
      x: 100,
      y: 100,
      fontSize: 32,
      color: '#000000'
    };
    setOverlays([...overlays, newOverlay]);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gray-900 border-b border-gray-800">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          Back
        </button>
        <h1 className="text-lg font-semibold text-white">Create Postcard Stamp</h1>
        <button
          onClick={generateFinalImage}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <CheckIcon className="h-4 w-4" />
          Save
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Image Preview Area */}
        <div className="flex-1 flex items-center justify-center p-4 bg-gray-900 min-h-0 overflow-hidden">
          <div className="relative max-w-full max-h-full overflow-hidden rounded-lg shadow-2xl">
            {selectedBorder !== 'none' ? (
              <canvas
                ref={previewCanvasRef}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            ) : (
              <img
                src={imageUrl}
                alt="Postcard"
                className="max-w-full max-h-full object-contain rounded-lg"
                style={{
                  maxWidth: '400px',
                  maxHeight: '500px',
                  filter: `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) blur(${blur}px) ${FILTERS.find(f => f.id === selectedFilter)?.filter || ''}`
                }}
              />
            )}
          </div>
        </div>

        {/* Controls Area */}
        <div className="bg-gray-800 border-t border-gray-700 p-4">
          {/* Tabs */}
          <div className="flex gap-2 mb-4 overflow-x-auto">
            <button
              onClick={() => setActiveTab('stamps')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                activeTab === 'stamps' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <RectangleStackIcon className="h-5 w-5" />
              Stamps
            </button>
            <button
              onClick={() => setActiveTab('adjust')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                activeTab === 'adjust' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <AdjustmentsHorizontalIcon className="h-5 w-5" />
              Adjust
            </button>
            <button
              onClick={() => setActiveTab('filters')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                activeTab === 'filters' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <SparklesIcon className="h-5 w-5" />
              Filters
            </button>
            <button
              onClick={() => setActiveTab('stickers')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                activeTab === 'stickers' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <FaceSmileIcon className="h-5 w-5" />
              Stickers
            </button>
          </div>

          {/* Tab Content */}
          <div className="max-h-64 overflow-y-auto">
            {activeTab === 'stamps' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="grid grid-cols-3 gap-3">
                  {STAMP_BORDERS.map((border) => (
                    <button
                      key={border.id}
                      onClick={() => setSelectedBorder(border.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        selectedBorder === border.id
                          ? 'border-blue-500 bg-blue-600'
                          : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                      }`}
                    >
                      <div className="w-full h-12 bg-gray-700 rounded mb-2 flex items-center justify-center">
                        <span className="text-2xl">{border.preview}</span>
                      </div>
                      <span className="text-xs text-gray-300">{border.name}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {activeTab === 'adjust' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div>
                  <label className="text-sm text-gray-300 mb-2 block">Brightness: {brightness}%</label>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={brightness}
                    onChange={(e) => setBrightness(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300 mb-2 block">Contrast: {contrast}%</label>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={contrast}
                    onChange={(e) => setContrast(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300 mb-2 block">Saturation: {saturation}%</label>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={saturation}
                    onChange={(e) => setSaturation(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300 mb-2 block">Blur: {blur}px</label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={blur}
                    onChange={(e) => setBlur(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
              </motion.div>
            )}

            {activeTab === 'filters' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-4 gap-3"
              >
                {FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => setSelectedFilter(filter.id)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      selectedFilter === filter.id
                        ? 'border-blue-500 bg-blue-600'
                        : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                    }`}
                  >
                    <span className="text-xs text-gray-300">{filter.name}</span>
                  </button>
                ))}
              </motion.div>
            )}

            {activeTab === 'stickers' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-6 gap-2"
              >
                {EMOJIS.map((emoji, index) => (
                  <button
                    key={index}
                    onClick={() => addOverlay('emoji', emoji)}
                    className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors text-2xl"
                  >
                    {emoji}
                  </button>
                ))}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
